// NATIVE
import React from 'react';

// INSTALLED
import { Grid } from '@mui/material';
import { useSelector } from 'react-redux';
import { useFormikContext } from 'formik';

// CUSTOM
import InputField from '../../formFields/InputField';
import TextAreaField from '../../formFields/TextAreaField';
import AutocompleteField from '../../formFields/autocompleteField';

/**
 * EditarArea component is responsible for rendering and managing the form fields
 * for editing an area. It utilizes Formik for form state management and Redux for
 * accessing the area data from the global state.
 *
 * @param {any} props - The properties passed to the component.
 * @returns {JSX.Element} The rendered component.
 *
 * @component
 * @example
 * const formField = {
 *   e_name: { name: 'name', label: 'Name' },
 *   e_description: { name: 'description', label: 'Description' }
 * };
 * return <EditarArea formField={formField} />;
 *
 * @remarks
 * This component uses the `useFormikContext` hook to access Formik's context and
 * the `useSelector` hook to access the Redux state. It also uses `useEffect` to
 * update the local state when the area data changes.
 *
 * @function setData - A helper function to set form field values based on the area data.
 *
 * @hook useFormikContext - To access Formik's context.
 * @hook useSelector - To access the Redux state.
 * @hook useState - To manage local state for the area data.
 * @hook useEffect - To update local state when the area data changes.
 */
export const EditarArea = (props: any) => {
  // USE FORMIK CONTEXT
  const formikProps: any = useFormikContext();

  // USE SELECTOR
  const { area }: any = useSelector((state: any) => state.areas);
  const { sucursales_list_select }: any = useSelector((state: any) => state.sucursales);

  // FORM FIELDS
  const {
    formField: { e_name, e_description, e_sucursal },
  } = props;

  // USE STATE
  const [dataArea, setDataArea] = React.useState<any>(null);

  // USE EFFECT
  React.useEffect(() => {
    setDataArea(area);
  }, [area]);

  React.useEffect(() => {
    setData(area);
  }, [area]);

  // CONST FUNCTIONS
  const setData = (areas: any) => {
    // ID COMPANY
    formikProps.setFieldValue('areaId', areas.id);

    // DATOS BASICOS
    formikProps.setFieldValue('e_name', areas.name);
    formikProps.setFieldValue('e_description', areas.description);
    formikProps.setFieldValue('e_sucursal', { label: areas.branch.name, value: areas.branch.id });
  };

  return (
    <div>
      {dataArea && (
        <>
          <Grid>
            <Grid size={12} sx={{ padding: 2 }}>
              <InputField
                name={e_name.name}
                label={e_name.label}
                variant="outlined"
                autoComplete="off"
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid>
            <Grid size={12} sx={{ padding: 2 }}>
              <TextAreaField
                name={e_description.name}
                label={e_description.label}
                variant="outlined"
                autoComplete="off"
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid>
            <Grid size={12} sx={{ padding: 2 }}>
              <AutocompleteField
                options={sucursales_list_select}
                name={e_sucursal.name}
                label={e_sucursal.label}
                element={e_sucursal.name}
              />
            </Grid>
          </Grid>
        </>
      )}
    </div>
  );
};
